import React, { useState, useRef, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { questionBankAPI } from "../../../services/api";
import ExcelValidationModal from "./ExcelValidationModal";
import SearchableSelect from "../../../components/shared/SearchableSelect";

const UploadQuestionBanksModal = ({
  open,
  onOpenChange,
  dropDownQuestionBank,
  onUploadSuccess,
  showToast,
  preSelectedQuestionBank = null,
}) => {
  const fileInputRef = useRef(null);
  const [selectedQuestionBank, setSelectedQuestionBank] = useState(null);
  const [validationData, setValidationData] = useState(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Separate user and company question banks
  const userQuestionBanks =
    dropDownQuestionBank?.filter((bank) => bank.type === "user") || [];
  const companyQuestionBanks =
    dropDownQuestionBank?.filter((bank) => bank.type === "company") || [];

  const validationFileSchema = Yup.object({
    questionBank: Yup.string().required("Question Bank is required"),
    file: Yup.mixed()
      .required("A file is required")
      .test(
        "fileSize",
        "File too large",
        (value) => !value || (value && value.size <= 5 * 1024 * 1024)
      ),
  });

  const fileFormik = useFormik({
    initialValues: {
      questionBank: preSelectedQuestionBank || "",
      file: null,
    },
    validationSchema: validationFileSchema,
    onSubmit: handleFileUpload,
  });

  useEffect(() => {
    if (preSelectedQuestionBank && dropDownQuestionBank?.length > 0) {
      const selected = dropDownQuestionBank.find(
        (item) => item._id === preSelectedQuestionBank
      );
      if (selected) {
        fileFormik.setFieldValue("questionBank", preSelectedQuestionBank);
        setSelectedQuestionBank(selected);
      }
    }
  }, [preSelectedQuestionBank, dropDownQuestionBank, open]);

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    fileFormik.setFieldValue("file", file);
  };

  const handleQuestionBankChange = (value) => {
    fileFormik.setFieldValue("questionBank", value);
    const selected = dropDownQuestionBank?.find(
      (item) => item._id === value || item.name === value
    );
    setSelectedQuestionBank(selected);
  };

  async function handleFileUpload(values) {
    fileFormik.resetForm();
    if (!values.file) return;

    setIsValidating(true);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const binaryStr = e.target.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        const headers = jsonData[0];
        const rows = jsonData.slice(1);

        const dataObjects = rows.map((row) =>
          Object.fromEntries(
            headers.map((header, index) => {
              let value = row[index] || null;
              return [header, value];
            })
          )
        );

        try {
          // Send to backend for validation
          const response = await questionBankAPI.upload({
            excelSheetData: dataObjects,
            selectedQuestionBank: selectedQuestionBank,
          });

          setValidationData(response.data);
          setShowValidationModal(true);
          onOpenChange(false);
          setIsValidating(false);
        } catch (error) {
          setIsValidating(false);
          console.error("Failed to validate excel:", error);

          // Handle capacity exceeded error specifically
          if (error?.response?.data?.error === "CAPACITY_EXCEEDED") {
            const capacityInfo = error.response.data.capacityInfo;
            showToast(
              "error",
              "Capacity Exceeded",
              `Question bank has insufficient space. Available: ${capacityInfo.available}, Requested: ${capacityInfo.requested}. Please reduce the number of questions or increase the question bank limit.`
            );
          } else {
            showToast(
              "error",
              "Error",
              error?.response?.data?.message || "Failed to validate excel"
            );
          }
        }
      };

      reader.readAsBinaryString(values.file);
    } catch (error) {
      setIsValidating(false);
      console.error("Failed to process file:", error);
      showToast("error", "Error", "Failed to process the uploaded file");
    }
  }

  const handleSubmitValidated = async (
    validatedQuestions,
    isPartial = false
  ) => {
    setIsSubmitting(true);

    try {
      await questionBankAPI.submitValidated({
        validatedQuestions,
        selectedQuestionBank,
        isPartial,
      });

      if (!isPartial) {
        showToast("success", "Success", "Questions uploaded successfully");
        setShowValidationModal(false);
        setValidationData(null);

        // Refetch question banks data
        if (onUploadSuccess) {
          onUploadSuccess();
        }

        fileFormik.resetForm();
        setSelectedQuestionBank(null);
      }
    } catch (error) {
      console.error("Failed to submit questions:", error);

      // Handle capacity exceeded error for submission
      if (error?.response?.data?.error === "CAPACITY_EXCEEDED") {
        const capacityInfo = error.response.data.capacityInfo;
        showToast(
          "error",
          "Capacity Exceeded",
          `Question bank has insufficient space. Available: ${capacityInfo.available}, Requested: ${capacityInfo.requested}. Some questions may have been uploaded already.`
        );
      } else {
        showToast(
          "error",
          "Error",
          error?.response?.data?.message || "Failed to submit questions"
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    fileFormik.resetForm();
    setSelectedQuestionBank(null);
  };

  const handleValidationModalClose = () => {
    setShowValidationModal(false);
    setValidationData(null);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload a sample excel sheet</DialogTitle>
            <DialogDescription>
              Upload a sample excel sheet for submitting.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={fileFormik.handleSubmit}>
            <div className="grid gap-4 py-4 mt-2">
              <div className="grid grid-row-2 gap-4">
                <div>
                  <Label htmlFor="questionBank">
                    Choose a question bank for excel uploading*
                  </Label>
                  {preSelectedQuestionBank ? (
                    <div className="p-2 border rounded-md bg-gray-50 text-gray-800">
                      {
                        Array.isArray(dropDownQuestionBank)
                          ? dropDownQuestionBank.find((item) => item._id === preSelectedQuestionBank)?.name
                          : "Selected Question Bank"
                      }
                    </div>
                  ) : (
                    < SearchableSelect
                      value={fileFormik.values.questionBank}
                      onValueChange={handleQuestionBankChange}
                      placeholder="Select Question Bank"
                      userItems={userQuestionBanks}
                      companyItems={companyQuestionBanks}
                      className="w-full"
                    />)}
                  {fileFormik.touched.questionBank &&
                    fileFormik.errors.questionBank && (
                      <p className="text-sm text-red-500 mt-1">
                        {fileFormik.errors.questionBank}
                      </p>
                    )}
                </div>
                <div>
                  <Label htmlFor="file">Upload File*</Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer p-2"
                  />
                  {fileFormik.touched.file && fileFormik.errors.file && (
                    <p className="text-sm text-red-500 mt-1">
                      {fileFormik.errors.file}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={isValidating}>
                {isValidating && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isValidating ? "Validating..." : "Proceed"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ExcelValidationModal
        open={showValidationModal}
        onOpenChange={handleValidationModalClose}
        validationData={validationData}
        selectedQuestionBank={selectedQuestionBank}
        onSubmitValidated={handleSubmitValidated}
        showToast={showToast}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default UploadQuestionBanksModal;
